import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
const _excluded = ["angle", "color", "endColor", "label", "opacity", "selected", "size", "startColor", "children"];
import { FocusProvider } from '../../utils/FocusProvider';
import { ActiveProvider } from '../../utils/useActive';
import { HoverProvider } from '../../utils/useHovered';
import { rgba } from '../../utils/color';
import styled from 'foundations-theming/utils';
import { Clickable } from '../../components/Clickable/Clickable';
import devLogger from 'react-utils/devLogger';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { getAfterStyles, getSquatchOuterStyles } from './ColorSquatch.helpers';

/**
 * @internal
 * **Do not use.** Ask about your use case in #hds-support.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const SQUATCH_SIZE = 20;
export const getGradient = (foregroundColor, gradientDirection, startColor, startColorStop, endColor, endColorStop) => {
  const fg = foregroundColor ? `color: ${foregroundColor};` : '';
  const bgColor = startColor ? `background-color: ${startColor};` : '';
  const gradient = `background-image: linear-gradient(${gradientDirection}, ${startColor} ${startColorStop || ''}, ${endColor} ${endColorStop || ''}) !important;`;
  return `${fg} ${bgColor} ${gradient}`;
};
const SquatchOuter = styled(_ref => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/_jsx(Clickable, Object.assign({}, rest));
}).withConfig({
  displayName: "ColorSquatch__SquatchOuter",
  componentId: "sc-1prs6kz-0"
})(["", ";&::after{", "}"], props => getSquatchOuterStyles(props), props => getAfterStyles(props));
const SquatchColor = styled.span.withConfig({
  displayName: "ColorSquatch__SquatchColor",
  componentId: "sc-1prs6kz-1"
})(({
  color,
  startColor,
  endColor,
  opacity,
  selected,
  angle,
  theme
}) => {
  return {
    background: startColor ? getGradient(undefined, `${angle || '0deg'}`, startColor, undefined, endColor, undefined) : color && `${color} !important`,
    borderWidth: '1px',
    borderColor: selected ? theme.color['fill-surface-default'] : rgba(theme.color['specialty-scrim'], 0.2),
    // TODO why are we changing the color via rgba() instead of using it as is given it already has opacity?
    borderStyle: 'solid',
    borderRadius: 'inherit',
    display: 'block',
    height: '100%',
    width: '100%',
    opacity: opacity ? opacity / 100 : undefined,
    pointerEvents: 'none'
  };
});
export const ColorSquatch = _ref2 => {
  let {
      angle = '90deg',
      color,
      endColor,
      label,
      opacity,
      selected,
      size = SQUATCH_SIZE,
      startColor,
      children
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const implicitLabel = label || color;
  const colorOrLabel = !label && startColor && endColor ? `${startColor}, ${endColor}` : implicitLabel;
  const computedLabel = `${colorOrLabel}${opacity ? `, ${opacity} opacity` : ''}`;
  if (process.env.NODE_ENV !== 'production') {
    if (startColor && !endColor) {
      devLogger.warn({
        message: 'ColorSquatch: Please set an endColor if using a startColor',
        key: 'ColorSquatch: no startColor without endColor'
      });
    }
    if (!startColor && endColor) {
      devLogger.warn({
        message: 'ColorSquatch: Please set a startColor if using an endColor',
        key: 'ColorSquatch: no endColor without startColor'
      });
    }
  }
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, rest, {
    children: hoverProviderProps => /*#__PURE__*/_jsx(ActiveProvider, Object.assign({}, rest, {
      children: activeProviderProps => /*#__PURE__*/_jsx(FocusProvider, Object.assign({}, rest, {
        children: focusProviderProps => /*#__PURE__*/_jsx(SquatchOuter, Object.assign({}, hoverProviderProps, activeProviderProps, focusProviderProps, {
          size: size,
          "aria-label": computedLabel,
          "aria-pressed": selected,
          opacity: opacity,
          selected: selected,
          $cssStyles: cssStyles
        }, domProps, callbackProps, {
          children: /*#__PURE__*/_jsx(SquatchColor, {
            angle: angle,
            color: color,
            endColor: endColor,
            opacity: opacity,
            startColor: startColor,
            children: children
          })
        }))
      }))
    }))
  }));
};
ColorSquatch.displayName = 'ColorSquatch';